import React, { useState, useContext, useEffect, useRef } from "react";
import { Dropdown, Modal, Form, Button, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import LogoutPage from './Logout';
import { IMAGES, SVGICON } from "../../constant/theme";
import { ThemeContext } from "../../../context/ThemeContext";
import { changePassword } from "../../../services/ChangePasswordService";
import { getProfile, updateProfile, uploadProfilePhoto } from "../../../services/ProfileSettingsService";
import { getSavedUser } from "../../../services/AuthService";
import { getOrders } from "../../../services/OrdersService";
import { getMaterialsStock } from "../../../services/MaterialsStockService";
import { getMaterialName } from "../../../utils/materialName";
import { translateDeliveryStatus, translateRole } from '../../../utils/i18nHelpers';

const NOTIFICATIONS_VIEWED_KEY = 'tabliya_notifications_last_viewed';

function getNotificationsViewedState() {
	try {
		const raw = localStorage.getItem(NOTIFICATIONS_VIEWED_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			return { lowStockCount: parsed.lowStockCount || 0, orderCount: parsed.orderCount || 0 };
		}
	} catch (_) {}
	return null;
}

function setNotificationsViewedState(lowStockCount, orderCount) {
	try {
		localStorage.setItem(NOTIFICATIONS_VIEWED_KEY, JSON.stringify({ lowStockCount, orderCount }));
	} catch (_) {}
}

function formatNotificationTime(isoStr) {
	if (!isoStr) return '';
	const d = new Date(isoStr);
	const now = new Date();
	const diffMs = now - d;
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMs / 3600000);
	const diffDays = Math.floor(diffMs / 86400000);
	if (diffMins < 1) return 'Just now';
	if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
	if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
	if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
	return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatStatus(s) {
	return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const OrderNotifications = ({ notifications }) => {
	if (!notifications || notifications.length === 0) {
		return null;
	}
	return (
		<>
			{notifications.map((n, i) => (
				<li key={`${n.orderId}-${n.type}-${n.timestamp}`}>
					<div className="timeline-panel">
						<div className={`media me-2 ${n.type === 'created' ? 'media-info' : 'media-success'}`} style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 600 }}>
							{n.type === 'created' ? 'N' : 'S'}
						</div>
						<div className="media-body">
							<Link to={`/orders/${n.orderId}`} className="text-decoration-none text-dark">
								<h6 className="mb-1">{n.message}</h6>
							</Link>
							<small className="d-block text-muted">{formatNotificationTime(n.timestamp)}</small>
						</div>
					</div>
				</li>
			))}
		</>
	);
};

const LowStockNotifications = ({ notifications, lowLabel, stockLabel }) => {
	if (!notifications || notifications.length === 0) {
		return null;
	}
	return (
		<>
			{notifications.map((n) => (
				<li key={`low-stock-${n.material_name}`}>
					<div className="timeline-panel">
						<div className="media me-2 media-danger" style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 600 }}>
							<i className="fa-solid fa-exclamation-triangle" />
						</div>
						<div className="media-body">
							<Link to="/materials-stock" className="text-decoration-none text-dark">
								<h6 className="mb-1">{lowLabel.replace('{{name}}', getMaterialName(n))}</h6>
							</Link>
							<small className="d-block text-muted">{stockLabel.replace('{{quantity}}', n.quantity).replace('{{unit}}', n.unit)}</small>
						</div>
					</div>
				</li>
			))}
		</>
	);
};

const Header = () => {
	const { t, i18n } = useTranslation();
	const [headerFix, setheaderFix] = useState(false);
	const [orderNotifications, setOrderNotifications] = useState([]);
	const [lowStockNotifications, setLowStockNotifications] = useState([]);
	const [notificationFilter, setNotificationFilter] = useState('all'); // 'all' | 'orders' | 'lowstock'
	const [notificationsViewedVersion, setNotificationsViewedVersion] = useState(0); // force re-read from localStorage after marking viewed
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [showProfileSettings, setShowProfileSettings] = useState(false);
	const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
	const [pwError, setPwError] = useState('');
	const [pwSuccess, setPwSuccess] = useState(false);
	const [profileForm, setProfileForm] = useState({ email: '', phone_number: '' });
	const [profilePhoto, setProfilePhoto] = useState(null);
	const [profileError, setProfileError] = useState('');
	const [profileSuccess, setProfileSuccess] = useState(false);
	const [profileLoading, setProfileLoading] = useState(false);
	const [displayUser, setDisplayUser] = useState(null);
	const savedUser = displayUser || getSavedUser();

	const loadOrderNotifications = () => {
		getOrders().then((res) => {
			const orders = res.data || [];
			const events = [];
			for (const o of orders) {
				const created = o.created_at || o.order_date;
				const updated = o.updated_at;
				const createdTs = created ? new Date(created).getTime() : 0;
				const updatedTs = updated ? new Date(updated).getTime() : 0;
				events.push({ type: 'created', orderId: o.id, orderNumber: o.order_number, message: t('header.orderCreated', { orderNumber: o.order_number }), timestamp: created });
				if (updatedTs > createdTs + 60000) {
					events.push({ type: 'status_changed', orderId: o.id, orderNumber: o.order_number, message: t('header.orderStatusChanged', { orderNumber: o.order_number, status: translateDeliveryStatus(o.delivery_status) || formatStatus(o.delivery_status) }), timestamp: updated });
				}
			}
			events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
			setOrderNotifications(events.slice(0, 15));
		}).catch(() => setOrderNotifications([]));
	};

	const loadLowStockNotifications = () => {
		getMaterialsStock().then((res) => {
			const stock = res.data || [];
			const lowStock = stock
				.filter((item) => {
					const qty = parseFloat(item.quantity) || 0;
					return qty <= 1;
				})
				.map((item) => ({
					material_name: item.material_name || 'Unknown',
					name_nl: item.name_nl || '',
					quantity: parseFloat(item.quantity) || 0,
					unit: item.unit || 'unit',
				}));
			setLowStockNotifications(lowStock);
		}).catch(() => setLowStockNotifications([]));
	};

	useEffect(() => {
		loadOrderNotifications();
		loadLowStockNotifications();
		const interval = setInterval(() => {
			loadLowStockNotifications();
		}, 60000);
		return () => clearInterval(interval);
	}, []);

	const openProfileSettings = async () => {
		setShowProfileSettings(true);
		setProfileError('');
		setProfileSuccess(false);
		try {
			const res = await getProfile();
			const p = res.data;
			setProfileForm({ email: p.email || '', phone_number: p.phone_number || '' });
			setProfilePhoto(p.profile_photo || null);
		} catch {
			setProfileForm({ email: savedUser?.email || '', phone_number: '' });
			setProfilePhoto(null);
		}
	};

	const resizeImage = (file, maxSize = 800, quality = 0.85) => {
		return new Promise((resolve) => {
			const img = new Image();
			const url = URL.createObjectURL(file);
			img.onload = () => {
				URL.revokeObjectURL(url);
				let { width, height } = img;
				if (width > maxSize || height > maxSize) {
					if (width > height) {
						height = Math.round((height * maxSize) / width);
						width = maxSize;
					} else {
						width = Math.round((width * maxSize) / height);
						height = maxSize;
					}
				}
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				ctx.drawImage(img, 0, 0, width, height);
				const dataUrl = canvas.toDataURL('image/jpeg', quality);
				resolve(dataUrl);
			};
			img.onerror = () => {
				URL.revokeObjectURL(url);
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result);
				reader.readAsDataURL(file);
			};
			img.src = url;
		});
	};

	const handleProfilePhotoChange = async (e) => {
		const file = e.target.files?.[0];
		if (!file || !file.type.startsWith('image/')) return;
		try {
			const resized = await resizeImage(file);
			setProfilePhoto(resized);
		} catch {
			const reader = new FileReader();
			reader.onload = () => setProfilePhoto(reader.result);
			reader.readAsDataURL(file);
		}
	};

	const handleProfileSubmit = async (e) => {
		e.preventDefault();
		setProfileError('');
		setProfileSuccess(false);
		setProfileLoading(true);
		try {
			if (profilePhoto) {
				await uploadProfilePhoto(profilePhoto);
			}
			await updateProfile({ email: profileForm.email, phone_number: profileForm.phone_number || null });
			setProfileSuccess(true);
			const res = await getProfile();
			const p = res.data;
			const updated = { ...getSavedUser(), ...p };
			localStorage.setItem('userDetails', JSON.stringify(updated));
			setDisplayUser(updated);
			setTimeout(() => setShowProfileSettings(false), 1200);
		} catch (err) {
			setProfileError(err.response?.data?.error || t('header.failedProfileUpdate'));
		} finally {
			setProfileLoading(false);
		}
	};

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setPwError('');
		if (pwForm.new !== pwForm.confirm) {
			setPwError(t('header.passwordMismatch'));
			return;
		}
		if (pwForm.new.length < 6) {
			setPwError(t('header.passwordMinLength'));
			return;
		}
		try {
			await changePassword(pwForm.current, pwForm.new);
			setPwSuccess(true);
			setTimeout(() => {
				setShowChangePassword(false);
				setPwForm({ current: '', new: '', confirm: '' });
				setPwSuccess(false);
			}, 1500);
		} catch (err) {
			setPwError(err.response?.data?.error || t('header.failedPasswordChange'));
		}
	};

	useEffect(() => {
		window.addEventListener("scroll", () => {
			setheaderFix(window.scrollY > 50);
		});
	}, []);

	const {background, changeBackground } = useContext(ThemeContext);
	const handleThemeMode = () => {
		if(background.value === 'dark'){
			changeBackground({ value: "light", label: "Light" });
		}else{
			changeBackground({ value: "dark", label: "Dark" });
		}
	}

	const toggleLanguage = () => {
		i18n.changeLanguage(i18n.resolvedLanguage === 'nl' ? 'en' : 'nl');
	};
  
  return ( 
    <div className={`header ${headerFix ? "is-fixed" : ""}`}>
      <div className="header-content">
        <nav className="navbar navbar-expand">
          	<div className="collapse navbar-collapse justify-content-between">
				<div className="header-left">					
				</div>
				<div className="header-right d-flex align-items-center">
					<ul className="navbar-nav ">	
						<li className="nav-item me-2">
							<button
								type="button"
								className="btn btn-outline-primary btn-sm"
								onClick={toggleLanguage}
								aria-label={t('language.switch')}
								title={`${t('language.english')} / ${t('language.dutch')}`}
							>
								{i18n.resolvedLanguage === 'nl' ? 'EN' : 'NL'}
							</button>
						</li>
						<li className="nav-item dropdown notification_dropdown">
							<Link to={'#'} className="nav-link bell dz-theme-mode"
 								onClick={()=>handleThemeMode()}
							>
								{background.value === "dark" ?
									<svg  xmlns="http://www.w3.org/2000/svg"  width="24px" height="24px" viewBox="0 0 24 24" version="1.1" className="svg-main-icon">
										<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
											<rect x="0" y="0" width="24" height="24"/>
											<path d="M12,15 C10.3431458,15 9,13.6568542 9,12 C9,10.3431458 10.3431458,9 12,9 C13.6568542,9 15,10.3431458 15,12 C15,13.6568542 13.6568542,15 12,15 Z" fill="currentColor" fillRule="nonzero"/>
											<path d="M19.5,10.5 L21,10.5 C21.8284271,10.5 22.5,11.1715729 22.5,12 C22.5,12.8284271 21.8284271,13.5 21,13.5 L19.5,13.5 C18.6715729,13.5 18,12.8284271 18,12 C18,11.1715729 18.6715729,10.5 19.5,10.5 Z M16.0606602,5.87132034 L17.1213203,4.81066017 C17.7071068,4.22487373 18.6568542,4.22487373 19.2426407,4.81066017 C19.8284271,5.39644661 19.8284271,6.34619408 19.2426407,6.93198052 L18.1819805,7.99264069 C17.5961941,8.57842712 16.6464466,8.57842712 16.0606602,7.99264069 C15.4748737,7.40685425 15.4748737,6.45710678 16.0606602,5.87132034 Z M16.0606602,18.1819805 C15.4748737,17.5961941 15.4748737,16.6464466 16.0606602,16.0606602 C16.6464466,15.4748737 17.5961941,15.4748737 18.1819805,16.0606602 L19.2426407,17.1213203 C19.8284271,17.7071068 19.8284271,18.6568542 19.2426407,19.2426407 C18.6568542,19.8284271 17.7071068,19.8284271 17.1213203,19.2426407 L16.0606602,18.1819805 Z M3,10.5 L4.5,10.5 C5.32842712,10.5 6,11.1715729 6,12 C6,12.8284271 5.32842712,13.5 4.5,13.5 L3,13.5 C2.17157288,13.5 1.5,12.8284271 1.5,12 C1.5,11.1715729 2.17157288,10.5 3,10.5 Z M12,1.5 C12.8284271,1.5 13.5,2.17157288 13.5,3 L13.5,4.5 C13.5,5.32842712 12.8284271,6 12,6 C11.1715729,6 10.5,5.32842712 10.5,4.5 L10.5,3 C10.5,2.17157288 11.1715729,1.5 12,1.5 Z M12,18 C12.8284271,18 13.5,18.6715729 13.5,19.5 L13.5,21 C13.5,21.8284271 12.8284271,22.5 12,22.5 C11.1715729,22.5 10.5,21.8284271 10.5,21 L10.5,19.5 C10.5,18.6715729 11.1715729,18 12,18 Z M4.81066017,4.81066017 C5.39644661,4.22487373 6.34619408,4.22487373 6.93198052,4.81066017 L7.99264069,5.87132034 C8.57842712,6.45710678 8.57842712,7.40685425 7.99264069,7.99264069 C7.40685425,8.57842712 6.45710678,8.57842712 5.87132034,7.99264069 L4.81066017,6.93198052 C4.22487373,6.34619408 4.22487373,5.39644661 4.81066017,4.81066017 Z M4.81066017,19.2426407 C4.22487373,18.6568542 4.22487373,17.7071068 4.81066017,17.1213203 L5.87132034,16.0606602 C6.45710678,15.4748737 7.40685425,15.4748737 7.99264069,16.0606602 C8.57842712,16.6464466 8.57842712,17.5961941 7.99264069,18.1819805 L6.93198052,19.2426407 C6.34619408,19.8284271 5.39644661,19.8284271 4.81066017,19.2426407 Z" fill="currentColor" fillRule="nonzero" opacity="0.3"/>
										</g>
									</svg>
									:
									<svg  xmlns="http://www.w3.org/2000/svg"  width="24px" height="24px" viewBox="0 0 24 24" version="1.1" className="svg-main-icon">
										<g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
											<rect x="0" y="0" width="24" height="24"/>
											<path d="M12.0700837,4.0003006 C11.3895108,5.17692613 11,6.54297551 11,8 C11,12.3948932 14.5439081,15.9620623 18.9299163,15.9996994 C17.5467214,18.3910707 14.9612535,20 12,20 C7.581722,20 4,16.418278 4,12 C4,7.581722 7.581722,4 12,4 C12.0233848,4 12.0467462,4.00010034 12.0700837,4.0003006 Z" fill="currentColor"/>
										</g>
									</svg>	
								}
							</Link>
						</li>	
						<Dropdown as="li" className="nav-item dropdown notification_dropdown" onToggle={(isOpen) => {
							if (isOpen) {
								loadOrderNotifications();
								loadLowStockNotifications();
								setNotificationFilter('all');
								// Mark as viewed: persist so badge stays hidden after refresh until there are new notifications
								setNotificationsViewedState(lowStockNotifications.length, orderNotifications.length);
								setNotificationsViewedVersion((v) => v + 1);
							}
						}}>
							<Dropdown.Toggle className="nav-link i-false c-pointer" variant="" as="a" style={{ position: 'relative' }}>
								{SVGICON.BellIcon}
								{(() => {
									const total = lowStockNotifications.length + orderNotifications.length;
									const lastViewed = getNotificationsViewedState();
									const alreadyViewed = lastViewed && lastViewed.lowStockCount >= lowStockNotifications.length && lastViewed.orderCount >= orderNotifications.length;
									const showBadge = total > 0 && !alreadyViewed;
									return showBadge ? (
										<span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem', padding: '2px 5px' }}>
											{total > 99 ? '99+' : total}
										</span>
									) : null;
								})()}
							</Dropdown.Toggle>
							<Dropdown.Menu align="end" className="mt-2 dropdown-menu dropdown-menu-end">
								<div className="widget-media dz-scroll p-3 height380">
									<ul className="timeline">
										{notificationFilter === 'orders' ? (
											orderNotifications.length === 0 ? (
										<li>
													<div className="timeline-panel">
														<div className="media-body">
															<h6 className="mb-1 text-muted">{t('header.noOrderNotificationsYet')}</h6>
															<small className="d-block">{t('header.noOrderNotificationsHint')}</small>
														</div>
													</div>
												</li>
											) : (
												<OrderNotifications notifications={orderNotifications} />
											)
										) : notificationFilter === 'lowstock' ? (
											lowStockNotifications.length === 0 ? (
												<li>
													<div className="timeline-panel">
														<div className="media-body">
															<h6 className="mb-1 text-muted">{t('header.noLowStockAlerts')}</h6>
															<small className="d-block">{t('header.noLowStockAlertsHint')}</small>
														</div>
													</div>
												</li>
											) : (
												<LowStockNotifications notifications={lowStockNotifications} lowLabel={t('header.materialIsLow', { name: '{{name}}' })} stockLabel={t('header.stockSummary', { quantity: '{{quantity}}', unit: '{{unit}}' })} />
											)
										) : lowStockNotifications.length === 0 && orderNotifications.length === 0 ? (
											<li>
												<div className="timeline-panel">
													<div className="media-body">
														<h6 className="mb-1 text-muted">{t('header.noNotificationsYet')}</h6>
														<small className="d-block">{t('header.noNotificationsHint')}</small>
													</div>
												</div>
											</li>
										) : (
											<>
												<LowStockNotifications notifications={lowStockNotifications} lowLabel={t('header.materialIsLow', { name: '{{name}}' })} stockLabel={t('header.stockSummary', { quantity: '{{quantity}}', unit: '{{unit}}' })} />
												<OrderNotifications notifications={orderNotifications} />
											</>
										)}
									</ul>
									<div className="ps__rail-x" style={{ left: 0, bottom: 0 }}>
										<div className="ps__thumb-x" tabIndex={0} style={{ left: 0, width: 0 }}/>
									</div>
									<div className="ps__rail-y" style={{ top: 0, right: 0 }}>
										<div className="ps__thumb-y" tabIndex={0} style={{ top: 0, height: 0 }}/>
									</div>
								</div>
								<div className="d-flex flex-column">
									{notificationFilter !== 'all' && (
										<a href="#" className="all-notification" onClick={(e) => { e.preventDefault(); setNotificationFilter('all'); }}>
											{t('header.showAllNotifications')} <i className="ti-arrow-right" />
										</a>
									)}
									{lowStockNotifications.length > 0 && (
										<Link className="all-notification" to="/materials-stock">
											{t('header.viewLowStock', { count: lowStockNotifications.length })} <i className="ti-arrow-right" />
										</Link>
									)}
									<a
										href="#"
										className="all-notification"
										onClick={(e) => { e.preventDefault(); setNotificationFilter('orders'); }}
									>
										{t('header.orderNotifications')} <i className="ti-arrow-right" />
									</a>
								</div>
							</Dropdown.Menu>
						</Dropdown>	
						<li className="nav-item ps-3">
							<Dropdown className="header-profile2">
								<Dropdown.Toggle className="nav-link i-false" as="div">
									<div className="header-info2 d-flex align-items-center">
										<div className="header-media rounded-circle bg-primary d-flex align-items-center justify-content-center text-white overflow-hidden" style={{ width: 40, height: 40 }}>
											{savedUser?.profile_photo ? (
												<img src={savedUser.profile_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											) : (
												<i className="fa-solid fa-user" style={{ fontSize: '1.1rem' }} />
											)}
										</div>										
									</div>
								</Dropdown.Toggle>
								<Dropdown.Menu align="end">
									<div className="card border-0 mb-0">
										<div className="card-header py-2">
											<div className="products">
												<div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white flex-shrink-0 overflow-hidden" style={{ width: 48, height: 48 }}>
													{savedUser?.profile_photo ? (
														<img src={savedUser.profile_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
													) : (
														<i className="fa-solid fa-user" style={{ fontSize: '1.25rem' }} />
													)}
												</div>
												<div className="ms-2">
													<h6>{savedUser?.username || savedUser?.email || t('header.roles.user')}</h6>
													<span>{translateRole(savedUser?.role)}</span>	
												</div>	
											</div>
										</div>
										<div className="card-body px-0 py-2">
											<Link to={"#"} className="dropdown-item ai-icon" onClick={(e) => { e.preventDefault(); openProfileSettings(); }}>
												{SVGICON.UserSvg}{" "}
												<span className="ms-2">{t('header.profileSettings')}</span>
											</Link>
											<Link to={"#"} className="dropdown-item ai-icon" onClick={(e) => { e.preventDefault(); setShowChangePassword(true); setPwError(''); }}>
												{SVGICON.Headersetting}{" "}
												<span className="ms-2">{t('header.changePassword')}</span>
											</Link>
										</div>
										<div className="card-footer px-0 py-2">
											<LogoutPage />
										</div>
									</div>
									
								</Dropdown.Menu>
							</Dropdown>
						</li>						
					</ul>
				</div>
			
          </div>
        </nav>
      </div>

      <Modal show={showProfileSettings} onHide={() => { setShowProfileSettings(false); setProfileError(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>{t('header.profileSettings')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleProfileSubmit}>
          <Modal.Body>
            {profileError && <Alert variant="danger">{profileError}</Alert>}
            {profileSuccess && <Alert variant="success">{t('header.profileUpdated')}</Alert>}
            <Form.Group className="mb-3 text-center">
              <Form.Label>{t('common.profilePhoto')}</Form.Label>
              <div className="d-flex justify-content-center align-items-center gap-3">
                <div className="rounded-circle overflow-hidden bg-primary d-flex align-items-center justify-content-center text-white" style={{ width: 80, height: 80 }}>
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <i className="fa-solid fa-user" style={{ fontSize: '2rem' }} />
                  )}
                </div>
                <Form.Control type="file" accept="image/*" onChange={handleProfilePhotoChange} className="w-auto" />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('common.email')}</Form.Label>
              <Form.Control type="email" required value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} placeholder={t('header.placeholders.email')} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('common.phoneNumber')}</Form.Label>
              <Form.Control type="tel" value={profileForm.phone_number} onChange={(e) => setProfileForm({ ...profileForm, phone_number: e.target.value })} placeholder={t('header.placeholders.phone')} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProfileSettings(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" type="submit" disabled={profileLoading}>
              {profileLoading ? t('common.loading') : t('common.saveChanges')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showChangePassword} onHide={() => { setShowChangePassword(false); setPwError(''); setPwForm({ current: '', new: '', confirm: '' }); }}>
        <Modal.Header closeButton>
          <Modal.Title>{t('header.changePassword')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangePassword}>
          <Modal.Body>
            {pwError && <Alert variant="danger">{pwError}</Alert>}
            {pwSuccess && <Alert variant="success">{t('header.passwordChanged')}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>{t('header.currentPassword')}</Form.Label>
              <Form.Control type="password" required value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} placeholder={t('header.currentPassword')} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('header.newPassword')}</Form.Label>
              <Form.Control type="password" required minLength={6} value={pwForm.new} onChange={(e) => setPwForm({ ...pwForm, new: e.target.value })} placeholder={t('header.passwordMinLength')} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('header.confirmPassword')}</Form.Label>
              <Form.Control type="password" required value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} placeholder={t('header.confirmPassword')} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowChangePassword(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" type="submit">{t('header.changePassword')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Header;
